import { CheckedState } from "@radix-ui/react-checkbox"
import { InvalidateQueryFilters, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import useUser from "@hooks/useUser"

import { Checkbox } from "@atoms/index"

import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import { Property } from "@molecules/index"

import { PropertyStack } from "@organisms/index"

import { Settings } from "@templates/index"

import {
    EstimatesNotificationTopics,
    NotificationMethods,
} from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import EmailNotificationTooltip from "@pages/Settings/SettingsNotifications/components/EmailNotificationTooltip/EmailNotificationTooltip"
import NotificationHeader from "@pages/Settings/SettingsNotifications/components/NotificationHeader/NotificationHeader"
import { useNotificationMethodsQuery } from "@pages/Settings/SettingsNotifications/components/NotificationMethodsSection/NotificationMethodsSection.store"
import SMSNotificationTooltip from "@pages/Settings/SettingsNotifications/components/SMSNotificationTooltip/SMSNotificationTooltip"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function EstimatesNotificationsForm() {
    const { user } = useUser()

    const [shouldEnableAllEmailNotifications, setShouldEnableAllEmailNotifications] = useState<boolean>(false)
    const [shouldDisableAllEmailNotifications, setShouldDisableAllEmailNotifications] = useState<boolean>(false)
    const [shouldEnableAllSmsNotifications, setShouldEnableAllSmsNotifications] = useState<boolean>(false)
    const [shouldDisableAllSmsNotifications, setShouldDisableAllSmsNotifications] = useState<boolean>(false)
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: estimatesNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<EstimatesNotificationTopics>("estimates")

    const { data: defaultMethodsPreference } = useNotificationMethodsQuery()

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!estimatesNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const handleToggleAllNotifications = (method: NotificationMethods, shouldEnableAllNotifications: CheckedState) => {
        if (method === "email") {
            setShouldDisableAllEmailNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllEmailNotifications(!!shouldEnableAllNotifications)
        } else {
            setShouldDisableAllSmsNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllSmsNotifications(!!shouldEnableAllNotifications)
        }
    }

    const phoneNotificationsDisabled = !user?.phone || !user?.phone_verified || !defaultMethodsPreference?.contact_sms
    const emailNotificationsDisabled =
        !user?.email || !user?.email_verified || !defaultMethodsPreference?.contact_email

    const modifyFieldset = (fieldset: EstimatesNotificationTopics, method: NotificationMethods, value: boolean) => {
        return Object.keys(fieldset).reduce((acc, key) => {
            const notificationKey = key as keyof EstimatesNotificationTopics
            if (notificationKey.endsWith(`_${method}`)) {
                acc[notificationKey] = value
            }
            return acc
        }, fieldset)
    }

    const formValues: EstimatesNotificationTopics | undefined = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "sms", false)
        } else if (shouldSetDefaultFormData) {
            return estimatesNotificationPreference
        } else {
            return undefined
        }
    }, [
        shouldSetDefaultFormData,
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const mutationTriggerPayload = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "sms", false)
        } else {
            return undefined
        }
    }, [
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const resetNotificationStates = () => {
        setShouldEnableAllEmailNotifications(false)
        setShouldDisableAllEmailNotifications(false)
        setShouldEnableAllSmsNotifications(false)
        setShouldDisableAllSmsNotifications(false)
    }

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries([notificationPreferenceQueryKey] as InvalidateQueryFilters)
    }

    const onAfterFormValuesSet = () => {
        resetNotificationStates()
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<EstimatesNotificationTopics, EstimatesNotificationTopics, Error, EstimatesNotificationTopics>
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            mutationTriggerPayload={mutationTriggerPayload}
            formValues={formValues}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {({ form }) => {
                const formValues = form.getValues()

                const emailValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_email"))
                    .map(([, value]) => value)

                const smsValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_sms"))
                    .map(([, value]) => value)

                const isAllNotificationsEmailEnabled = emailValues.every(Boolean)
                const isAllNotificationsSmsEnabled = smsValues.every(Boolean)
                const isSomeNotificationsEmailEnabled = emailValues.some(Boolean)
                const isSomeNotificationsSmsEnabled = smsValues.some(Boolean)

                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <NotificationHeader.Root>
                            <NotificationHeader.EmailCheckbox
                                isIndeterminate={isSomeNotificationsEmailEnabled && !isAllNotificationsEmailEnabled}
                                isChecked={isAllNotificationsEmailEnabled}
                                onChecked={(checked) => handleToggleAllNotifications("email", checked)}
                            />
                            <NotificationHeader.SMSCheckbox
                                isIndeterminate={isSomeNotificationsSmsEnabled && !isAllNotificationsSmsEnabled}
                                isChecked={isAllNotificationsSmsEnabled}
                                onChecked={(checked) => handleToggleAllNotifications("sms", checked)}
                            />
                        </NotificationHeader.Root>
                        <PropertyStack>
                            <Property.Root>
                                <Property.Content>
                                    <Property.Label>Estimate approved</Property.Label>
                                    <Property.Subtitle>
                                        Notify me when a client approves an estimate.
                                    </Property.Subtitle>
                                </Property.Content>
                                <Property.Value>
                                    <Settings.NotificationConfigPage.Checkboxes>
                                        <EmailNotificationTooltip>
                                            <Checkbox<EstimatesNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                name="enable_estimate_notification_estimate_approved_email"
                                                isControlled={true}
                                                isDisabled={isLoadingData || emailNotificationsDisabled}
                                            />
                                        </EmailNotificationTooltip>
                                        <SMSNotificationTooltip>
                                            <Checkbox<EstimatesNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                name="enable_estimate_notification_estimate_approved_sms"
                                                isControlled={true}
                                                isDisabled={isLoadingData || phoneNotificationsDisabled}
                                            />
                                        </SMSNotificationTooltip>
                                    </Settings.NotificationConfigPage.Checkboxes>
                                </Property.Value>
                            </Property.Root>
                            <Property.Root>
                                <Property.Content>
                                    <Property.Label>Estimate declined</Property.Label>
                                    <Property.Subtitle>
                                        Notify me when a client declines an estimate.
                                    </Property.Subtitle>
                                </Property.Content>
                                <Property.Value>
                                    <Settings.NotificationConfigPage.Checkboxes>
                                        <EmailNotificationTooltip>
                                            <Checkbox<EstimatesNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                name="enable_estimate_notification_estimate_declined_email"
                                                isControlled={true}
                                                isDisabled={isLoadingData || emailNotificationsDisabled}
                                            />
                                        </EmailNotificationTooltip>
                                        <SMSNotificationTooltip>
                                            <Checkbox<EstimatesNotificationTopics>
                                                size="md"
                                                colorScheme="gray"
                                                name="enable_estimate_notification_estimate_declined_sms"
                                                isControlled={true}
                                                isDisabled={isLoadingData || phoneNotificationsDisabled}
                                            />
                                        </SMSNotificationTooltip>
                                    </Settings.NotificationConfigPage.Checkboxes>
                                </Property.Value>
                            </Property.Root>
                        </PropertyStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
